import { Model } from '@/clients/http'

import { ProductHttpParams, ProductResponses } from '@/types'

export class ProductModel extends Model {
  // Define correlation keys for Redux state management
  public static readonly correlations = {
    GET_LIST: 'GET_PRODUCT_LIST',
    GET_BY_ID: 'GET_PRODUCT_BY_ID',
    CREATE: 'CREATE_PRODUCT',
    UPDATE: 'UPDATE_PRODUCT',
    DELETE: 'DELETE_PRODUCT',
    IMPORT: 'IMPORT_PRODUCTS', // Added correlation key for import
  }

  // Define endpoint builders
  public static readonly endpoints = {
    getList: (props?: ProductHttpParams.GetList) =>
      this.createEndpoint({
        url: '/api/backoffice/product',
        params: props?.params,
        query: props?.query,
      }),

    getById: (props?: ProductHttpParams.GetById) =>
      this.createEndpoint({
        url: '/api/backoffice/product/:id',
        params: props?.params,
        query: props?.query,
      }),

    create: (props?: ProductHttpParams.Create) =>
      this.createEndpoint({
        url: '/api/backoffice/product',
        params: props?.params,
        query: props?.query,
      }),

    update: (props?: ProductHttpParams.Update) =>
      this.createEndpoint({
        url: '/api/backoffice/product/:id',
        params: props?.params,
        query: props?.query,
      }),

    delete: (props?: ProductHttpParams.Delete) =>
      this.createEndpoint({
        url: '/api/backoffice/product/:id',
        params: props?.params,
        query: props?.query,
      }),

    // Endpoint builder for import
    import: (props?: ProductHttpParams.Import) =>
      this.createEndpoint({
        url: '/api/backoffice/product-import',
        params: props?.params,
        query: props?.query,
      }),

    // Endpoint builder for export
    export: (props?: any) =>
      this.createEndpoint({
        url: '/api/backoffice/product-export',
        params: props?.params,
        query: props?.query,
      }),
  }

  /**
   * Export products to file
   */
  public static readonly export = async (props?: any) => {
    return this.createHttpRequest<Blob, any>({
      correlationKey: 'EXPORT_PRODUCTS',
      payload: {
        method: 'get',
        url: this.endpoints.export(props),
        useBearerToken: true,
        responseType: 'blob',
      },
      store: props?.store,
    })
  }

  /**
   * Create a new product
   */
  public static readonly create = async (props: ProductHttpParams.Create) => {
    return this.createHttpRequest<ProductResponses.Create, ProductResponses.CreateError>({
      correlationKey: this.correlations.CREATE,
      payload: {
        method: 'post',
        url: this.endpoints.create(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Get paginated list of products
   */
  public static readonly getList = async (props?: ProductHttpParams.GetList) => {
    return this.createHttpRequest<ProductResponses.GetList, ProductResponses.GetListError>({
      correlationKey: this.correlations.GET_LIST,
      payload: {
        method: 'get',
        url: this.endpoints.getList(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Get a single product by ID
   */
  public static readonly getById = async (props: ProductHttpParams.GetById) => {
    return this.createHttpRequest<ProductResponses.GetById, ProductResponses.GetByIdError>({
      correlationKey: this.correlations.GET_BY_ID,
      payload: {
        method: 'get',
        url: this.endpoints.getById(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Update an existing product
   */
  public static readonly update = async (props: ProductHttpParams.Update) => {
    return this.createHttpRequest<ProductResponses.Update, ProductResponses.UpdateError>({
      correlationKey: this.correlations.UPDATE,
      payload: {
        method: 'put',
        url: this.endpoints.update(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Delete a product
   */
  public static readonly delete = async (props: ProductHttpParams.Delete) => {
    return this.createHttpRequest<ProductResponses.Delete, ProductResponses.DeleteError>({
      correlationKey: this.correlations.DELETE,
      payload: {
        method: 'delete',
        url: this.endpoints.delete(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Import products from file
   */
  public static readonly import = async (props: ProductHttpParams.Import) => {
    return this.createHttpRequest<ProductResponses.Import, any>({
      correlationKey: this.correlations.IMPORT, // Use the new correlation key
      payload: {
        method: 'post',
        url: this.endpoints.import(props),
        body: props.body, // This will be FormData
        useBearerToken: true,
        multipart: true,
      },
      store: props?.store,
    })
  }
}
